import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ArrowDownward, ArrowUpward, ArrowsSort } from "@livechat/design-system-icons";
import { Icon, Info, SpacingToken, Tooltip } from "@livechat/design-system-react-components";

import { newFeaturesCampaigns } from "constants/campaigns";
import { Column } from "constants/tickets/columns";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { TicketSortingField, TicketSortingOrder } from "interfaces/sorting";
import { selectFeatureToggleSortOrder } from "store/entities/license";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getRoute, selectActiveCustomViewId, selectCurrentSorting } from "store/views/tickets/selectors";
import { withVar } from "themes/helpers";

import { Th } from "./tickets-styles";

interface IProps {
  column: Column;
}

const SORTABLE_COLUMNS = [Column.LastMessageDate];
const MAP_COLUMN_TO_SORT_FIELD = {
  [Column.LastMessageDate]: TicketSortingField.LastMessageAt,
};

const getCorrespondingSortField = (column: Column): TicketSortingField | null =>
  column in MAP_COLUMN_TO_SORT_FIELD ? MAP_COLUMN_TO_SORT_FIELD[column as keyof typeof MAP_COLUMN_TO_SORT_FIELD] : null;

export function labelFactory(column: Column): string {
  switch (column) {
    case Column.Requester:
      return "Requester";

    case Column.Subject:
      return "Subject";

    case Column.UnrepliedSince:
      return "Unreplied since";

    case Column.Assignee:
      return "Agent";

    case Column.LastMessageDate:
      return "Last message";

    case Column.LastActivity:
      return "Last activity";

    case Column.Status:
      return "Status";

    case Column.Checkbox:
      return "";

    case Column.Options:
      return "";

    case Column.Mobile:
      return "";

    case Column.MobileOptions:
      return "";

    default:
      throw Error(`${column} column not supported`);
  }
}

export const TicketLabel: FC<IProps> = ({ column }) => {
  const featureToggleSortOrder = useSelector(selectFeatureToggleSortOrder);

  const currentView = useSelector(selectActiveCustomViewId);
  const ticketsRoute = useSelector(getRoute);

  const { isNewForAgent, featureFlagValue, setAgentFlagCallback } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenSortingTooltip.flag,
    newFeaturesCampaigns.hasSeenSortingTooltip.campaignStartDate
  );
  const isTooltipVisible =
    isNewForAgent && !featureFlagValue && column === Column.LastMessageDate && featureToggleSortOrder;

  const closeTooltip = (): void => {
    if (isTooltipVisible) {
      setAgentFlagCallback();
    }
  };

  const dispatch = useDispatch();
  const { field, order } = useSelector(selectCurrentSorting);
  const correspondingSortField = getCorrespondingSortField(column);

  const isCurrentSorting = correspondingSortField && correspondingSortField === field;

  const isSortable = SORTABLE_COLUMNS.includes(column);

  const sortDirection = isCurrentSorting
    ? order === TicketSortingOrder.Ascending
      ? ArrowUpward
      : ArrowDownward
    : ArrowsSort;

  const handleSort = (): void => {
    if (!correspondingSortField) {
      return;
    }
    closeTooltip();
    if (!isCurrentSorting) {
      dispatch(
        TicketsViewActions.updateCurrentSorting({ field: correspondingSortField, order: TicketSortingOrder.Ascending })
      );
    } else {
      dispatch(
        TicketsViewActions.updateCurrentSorting({
          field,
          order: order === TicketSortingOrder.Ascending ? TicketSortingOrder.Descending : TicketSortingOrder.Ascending,
        })
      );
    }
    trackEngagement("Tickets: Sorting changed", EventPlace.Tickets, {
      fromField: field,
      fromOrder: order === TicketSortingOrder.Ascending ? "ascending" : "descending",
      toField: correspondingSortField,
      toOrder: order === TicketSortingOrder.Ascending ? "descending" : "ascending",
      isCustomView: !!currentView,
      route: ticketsRoute,
    });
  };

  const shouldHaveSortIcon = isSortable && featureToggleSortOrder;
  const shouldShowSortIconOnHover = isSortable && !isCurrentSorting;

  return (
    <Th column={column} uppercase>
      <Tooltip
        isVisible={isTooltipVisible}
        onClose={closeTooltip}
        triggerOnHover={false}
        closeOnTriggerBlur={false}
        useDismissHookProps={{
          outsidePress: false,
        }}
        kind="important"
        placement="bottom-end"
        triggerRenderer={
          <LabelContent
            css={shouldShowSortIconOnHover && showIconOnHoverStyles}
            as={shouldHaveSortIcon ? "button" : undefined}
            onClick={shouldHaveSortIcon ? handleSort : undefined}
          >
            {labelFactory(column)}
            {shouldHaveSortIcon && (
              <Icon
                size="small"
                source={sortDirection}
                data-testid={`sort-icon-${column}${isCurrentSorting ? `-${order}` : ""}`}
              />
            )}
          </LabelContent>
        }
      >
        <Info
          css={css`
            width: 230px;
            text-transform: none;
          `}
          header="Sort tickets easily!"
          text="Click this column header to arrange the ticket list by date, showing either the newest or oldest tickets at the top."
          closeWithX
          handleCloseAction={closeTooltip}
        />
      </Tooltip>
    </Th>
  );
};

const LabelContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  color: inherit;
  background-color: inherit;
  border: none;
  font-size: inherit;
  text-transform: inherit;
  padding: 0;
  gap: ${withVar(SpacingToken.Spacing1)};
`;

const showIconOnHoverStyles = css`
  & > span {
    opacity: 0;
  }
  &:hover > span {
    opacity: 0.5;
  }
`;
